// Client-safe mirror of functions/lib/voices.ts.
//
// We only need name + vibe + gender for the dashboard UI (the actual
// ElevenLabs voice_id stays server-side and is never useful to render).
// Order must match functions/lib/voices.ts so the deterministic
// FNV-1a -> index assignment renders the same voice on client + server.
//
// If you swap a voice in functions/lib/voices.ts, mirror the change here.

export interface ClientVoice {
  name: string;
  gender: "m" | "f" | "n";
  vibe: string;
}

export const VOICE_POOL: readonly ClientVoice[] = [
  { name: "Rachel",    gender: "f", vibe: "warm" },
  { name: "Domi",      gender: "f", vibe: "spirited" },
  { name: "Bella",     gender: "f", vibe: "soft" },
  { name: "Antoni",    gender: "m", vibe: "calm" },
  { name: "Elli",      gender: "f", vibe: "youthful" },
  { name: "Josh",      gender: "m", vibe: "casual" },
  { name: "Arnold",    gender: "m", vibe: "gruff" },
  { name: "Adam",      gender: "m", vibe: "warm" },
  { name: "Sam",       gender: "m", vibe: "neutral" },
  { name: "Dorothy",   gender: "f", vibe: "kind" },
  { name: "James",     gender: "m", vibe: "british" },
  { name: "Thomas",    gender: "m", vibe: "thoughtful" },
  { name: "Joseph",    gender: "m", vibe: "british" },
  { name: "Ethan",     gender: "m", vibe: "young" },
  { name: "Chris",     gender: "m", vibe: "friendly" },
  { name: "Brian",     gender: "m", vibe: "deep" },
  { name: "Daniel",    gender: "m", vibe: "british" },
  { name: "Bill",      gender: "m", vibe: "older" },
  { name: "Paul",      gender: "m", vibe: "documentary" },
  { name: "Liam",      gender: "m", vibe: "articulate" },
  { name: "Will",      gender: "m", vibe: "youthful" },
  { name: "Jessica",   gender: "f", vibe: "expressive" },
  { name: "Eric",      gender: "m", vibe: "neutral" },
  { name: "Lily",      gender: "f", vibe: "british" },
  { name: "Charlotte", gender: "f", vibe: "sultry" },
  { name: "Alice",     gender: "f", vibe: "british" },
  { name: "Matilda",   gender: "f", vibe: "warm" },
  { name: "Aria",      gender: "f", vibe: "calm" },
  { name: "Sarah",     gender: "f", vibe: "smooth" },
  { name: "John",      gender: "m", vibe: "professional" },
  { name: "Laura",     gender: "f", vibe: "upbeat" },
  { name: "Charlie",   gender: "m", vibe: "australian" },
  { name: "George",    gender: "m", vibe: "british" },
  { name: "Callum",    gender: "m", vibe: "scottish" },
  { name: "River",     gender: "n", vibe: "androgynous" },
  { name: "Will",      gender: "m", vibe: "modern" },
  { name: "Lucy",      gender: "f", vibe: "energetic" },
  { name: "Jacob",     gender: "m", vibe: "narration" },
  { name: "Owen",      gender: "m", vibe: "young" },
  { name: "Maya",      gender: "f", vibe: "calm" },
  { name: "Diego",     gender: "m", vibe: "latino" },
  { name: "Sofia",     gender: "f", vibe: "spanish" },
  { name: "Hiro",      gender: "m", vibe: "japanese" },
  { name: "Yuki",      gender: "f", vibe: "japanese" },
  { name: "Mateus",    gender: "m", vibe: "brazilian" },
  { name: "Anya",      gender: "f", vibe: "russian" },
  { name: "Sven",      gender: "m", vibe: "nordic" },
  { name: "Mei",       gender: "f", vibe: "chinese" },
  { name: "Akira",     gender: "n", vibe: "calm" },
  { name: "Zara",      gender: "f", vibe: "british" },
];

// FNV-1a 32-bit. Identical implementation to functions/lib/voices.ts.
function fnv1a(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h >>> 0;
}

function normSlug(s: string): string {
  return (s || "anon").trim().toLowerCase().replace(/^@/, "") || "anon";
}

export function voiceForHandle(handle: string): ClientVoice {
  return VOICE_POOL[fnv1a(normSlug(handle)) % VOICE_POOL.length];
}
